/**
 * EdTech category name conversion.
 * Maps category_key and variants to official names from edtech_category_official_names.csv
 */

/** category_key -> official_name (from CSV) - full names for Category filters */
export const CATEGORY_KEY_TO_OFFICIAL = {
  academic_integrity: 'Academic Integrity / Plagiarism Detection',
  accessibility: 'Accessibility / Assistive-Tech Suites',
  adaptive_learning: 'Adaptive Learning / Authoring + Platform',
  admissions_crm: 'Student Recruitment & Engagement CRM',
  ai_assistants: 'AI & Virtual Assistants',
  alumni_advancement: 'Alumni & Advancement Systems',
  analytics_bi: 'Analytics & BI',
  attendance_tracking: 'Attendance Monitoring & Engagement Tracking',
  campus_mobile_id: 'Campus Mobile ID & Commerce',
  campus_safety: 'Campus Safety & Security Apps',
  careers_employability: 'Career Services & Employability',
  cloud_services: 'Cloud Services',
  course_evaluation: 'Feedback & Evaluation',
  credentialing: 'Digital Credentialing & Badging',
  curriculum_enrichment_management: 'Curriculum Enrichment & Curriculum Management Platforms',
  cybersecurity: 'Cybersecurity & Compliance Platforms',
  defense_technology: 'Defense Technology',
  digital_course_content: 'Digital Course Content & Inclusive Access',
  // [2026-10-16] 4Science's other two lens categories were missing here. Same strings as the BE
  // taxonomy display names and closed_deals.category.
  digital_preservation: 'Digital Preservation & Archives',
  digital_repository: 'Institutional & Digital Repositories',
  eportfolio: 'e-Portfolio / Comprehensive Learner Record',
  elearning_authoring_services: 'E-learning Content / Authoring / Learning Services',
  faculty_workload: 'Faculty Information & Workload Systems',
  finance_erp: 'Finance & ERP',
  generic_it: 'Generic IT',
  ict_investment: 'ICT Investment',
  integration_data: 'Integration & Data Platforms',
  it_services: 'IT Services',
  itsm_workflow: 'IT Service Management (ITSM) & Workflow Automation',
  lab_equipment: 'Lab Equipment',
  language_learning: 'Language Learning',
  it_hardware: 'IT Hardware',
  lecture_capture: 'Lecture Capture & Video CMS',
  library: 'Library & Discovery Services / Reading List Systems',
  library_self_service: 'Library Self-Service, RFID & Automated Handling',
  lms: 'Learning Management Systems (LMS)',
  marking: 'Marking / Grading Platforms',
  network_infrastructure: 'Infrastructure (Hosting)',
  online_course_marketplaces: 'Online Course Marketplaces & External Learning Platforms',
  online_exams: 'Online Exam Platforms',
  polling: 'Classroom Engagement & Polling',
  proctoring: 'Remote Proctoring',
  research_data: 'Research & Data Management Platforms',
  // [2026-10-15] CRIS carved out of research_data (4Science tenant). Same string as the BE taxonomy
  // display name and closed_deals.category.
  research_information_cris: 'Research Information Management (CRIS)',
  // [2026-10-15] Same string as the BE taxonomy display name and closed_deals.category, so
  // Procurement filters and Product Fit pickers match deals exactly (no bridge needed).
  robotics_automation_training: 'Robotics, Automation & Industry 4.0 Training',
  reading_literacy_platforms: 'Reading & Literacy Platforms',
  sis: 'Student Information Systems (SIS)',
  student_case_management: 'Student Case Management & Retention CRM',
  // [2026-10-23] Split out of student_success in the BE on 2026-10-22 (api/src/config/taxonomy.ts:195)
  // and now carrying 3,018 vendor cells for the blackbullion lens. Without an entry here
  // toOfficialCategoryName() falls through every branch to `return s`, so the raw slug
  // 'student_financial_wellbeing' rendered wherever a category label is shown.
  // Label is character-for-character the BE's CATEGORY_DISPLAY value - a variant name would be
  // invisible to every join.
  student_financial_wellbeing: 'Student Financial Wellbeing & Hardship Funding',
  student_housing: 'Student Housing & Accommodation Systems',
  student_payments: 'Student Payments & Financial Aid Platforms',
  student_success: 'Student Success & Engagement',
  talent_management_hcm: 'Talent Management / HCM',
  timetabling: 'Timetabling, Scheduling & Space Management',
  virtual_classroom: 'Virtual Classroom & Video-Conferencing',
  virtual_labs: 'Virtual Labs & STEM Simulations',
  website_development: 'Website Development',
  xr_immersive: 'XR / Immersive Learning Platforms',
  telemedicine: 'Telemedicine',
  patient_engagement_remote_monitoring: 'Patient Engagement & Remote Monitoring',
  omr_document_scanning: 'OMR & Document Scanning Hardware',
  // [2026-10-17] ELEVEN BE TAXONOMY CATEGORIES WERE ABSENT FROM THIS MAP WHILE LIVE DEALS CARRIED
  // THEM. Found while making bundled (multi-category) deals retrievable: a deal reachable only
  // through `matched_categories` arrives carrying a taxonomy KEY, and a key with no entry here
  // falls through toOfficialCategoryName() to the raw slug ('it_infrastructure'), which matches no
  // filter option and no lens label — so the row is fetched and then silently dropped by the
  // client filter. Labels taken VERBATIM from api/src/config/taxonomy.ts (CATEGORY_DISPLAY), which
  // is the authority; every one of these also appears as a `deals.category` label, so without the
  // entry the key and the label resolved to two different strings for the same category.
  // Live-row occurrences at the time of writing, highest first.
  it_infrastructure: 'IT Infrastructure / Storage',                                   // 154
  reading_literacy: 'Reading & Literacy Platforms',                                   //  84
  lxp_workforce: 'LXP/LMS (Workforce Learning)',                                      //  59
  ecm_document_esign: 'Document Management / ECM & e-Signature',                      //  42
  learning_analytics: 'Learning Analytics / Adaptive Insights',                       //  37
  creative_design_software: 'Creative & Design Software',                             //  31
  discipline_software: 'Discipline-Specific Teaching & Research Software',             //  14
  stem_robotics: 'STEM, Coding & Robotics Kits',                                      //   5
  clinical_placement: 'Clinical Placement & Competency Tracking',                     //   4
  procurement_platform: 'Procurement & eSourcing Platforms',                          //   4
  study_abroad_intl: 'Study Abroad & International Student Management',               //   3

  // [2026-10-21] SEVEN MORE, found by diffing this map against the BE's CATEGORY_DISPLAY rather
  // than waiting for each to surface. Tiago saw the Category column render
  // 'Institutional Effectiveness & Accreditation | institutional_effectiveness_accreditation' —
  // the label beside its own unresolved key, because LiveDeals.jsx joins `category` with
  // `matched_categories` and an unmapped key falls through to the raw slug. Only that one key was
  // live on a deal (2 cells); the other six are latent and would have produced the same defect the
  // first time a loader used them. Labels VERBATIM from api/src/config/taxonomy.ts.
  device_management: 'Device Management / MDM & Endpoint Fleet',
  grc_compliance: 'Governance, Risk & Compliance (GRC)',
  identity_access: 'Identity & Access Management',
  institutional_effectiveness_accreditation: 'Institutional Effectiveness & Accreditation',
  interactive_display: 'Interactive Displays & Classroom AV',
  marketing_automation: 'Marketing Automation & Campaign Tools',
  print_management: 'Print Management & Output Control',
};

/** Alternate keys that share an official taxonomy label. */
export const CATEGORY_KEY_ALIASES = {
  library_systems: 'library',
};

function canonicalCategoryKey(key) {
  return CATEGORY_KEY_ALIASES[key] || key;
}

/** Variant strings (from deals + closed_deals) -> official name */
const CATEGORY_VARIANTS = {
  'accessibility / assistive tech': 'Accessibility / Assistive-Tech Suites',
  'accessibility / assistive-tech': 'Accessibility / Assistive-Tech Suites',
  'admissions / marketing (opm / recruitment partner)': 'Student Recruitment & Engagement CRM',
  'admissions engagement / communications & case-management platform': 'Student Recruitment & Engagement CRM',
  'ai chatbots & virtual assistants': 'AI & Virtual Assistants',
  'ai chatbots & virtual tutors': 'AI & Virtual Assistants',
  'alumni / advancement': 'Alumni & Advancement Systems',
  'after-hours access (open library tech)': 'Library Self-Service, RFID & Automated Handling',
  'attendance analytics & intervention (k-12)': 'Student Success & Engagement',
  'career mapping / pathways platform': 'Career Services & Employability',
  'classroom av & interactive panels': 'Classroom Engagement & Polling',
  'classroom technology (aula digital)': 'Classroom Engagement & Polling',
  'cloud services': 'Cloud Services',
  'course & teaching evaluation': 'Feedback & Evaluation',
  'course authoring platform': 'E-learning Content / Authoring / Learning Services',
  'device lending (kiosks)': 'Campus Mobile ID & Commerce',
  'digital credentials & pathways platform': 'Digital Credentialing & Badging',
  'digital collections & digitization services': 'Digital Course Content & Inclusive Access',
  'digital collections & repositories (iiif/madoc)': 'Digital Course Content & Inclusive Access',
  'digital content subscriptions (public library e-resources)': 'Digital Course Content & Inclusive Access',
  'digital services / software': 'IT Services',
  'discovery layer & web cms': 'Library & Discovery Services / Reading List Systems',
  'e-portfolio / clr': 'e-Portfolio / Comprehensive Learner Record',
  'finance & erp': 'Finance & ERP',
  'finance and erp': 'Finance & ERP',
  'edtech': 'IT Services',
  'end-user devices (computers/printers)': 'IT Services',
  'end-user devices (computers/tablets)': 'IT Services',
  'enterprise software licensing (microsoft 365 / ees)': 'IT Services',
  'ict centre (k-12 infrastructure)': 'Infrastructure (Hosting)',
  'it equipment (education)': 'IT Services',
  'it equipment for instructional labs': 'Lab Equipment',
  'it hardware': 'IT Services',
  // [2026-10-05] The BE taxonomy (api/src/config/taxonomy.ts) spells the it_hardware key
  // 'IT Hardware (Education only)' and every deals.category row holds that string, while
  // CATEGORY_KEY_TO_OFFICIAL above calls the same key 'IT Hardware'. With no bridge the two never
  // met: getDealCategoriesForTenant('jpik') returned ['IT Hardware'], categoryOptions held
  // 'IT Hardware (Education only)', and the exact-match narrowing in Procurement.jsx left
  // preferredLiveCategories EMPTY — which made My Deals render as (0) and seeded no category on
  // tab activation, despite 1,481 live deals in the category. jpik is the first tenant whose
  // category label differs between the two repos.
  'it hardware (education only)': 'IT Hardware',
  // [2026-10-17] THE SAME DEFECT, FOUR MORE TIMES — it_hardware was not the only key whose BE
  // display name differs from the FE official name. Measured against taxonomy.ts: the BE suffixes
  // "(Education only)" on four more categories, and `deals.category` holds the suffixed string
  // while `matched_categories` holds the bare key, so a bundled deal's key resolved to
  // 'Cloud Services' while the tenant lens and the primary label resolved to
  // 'Cloud Services (Education only)' — two strings for one category, and the filter matched
  // neither against the other. 7,075 live-row occurrences across the five, it_services being
  // 5,892 of them. An ALIAS, never a rename: renaming CATEGORY_KEY_TO_OFFICIAL would change
  // display text estate-wide (the 2026-10-05 note above is explicit about this).
  'it services (education only)': 'IT Services',
  'cloud services (education only)': 'Cloud Services',
  'lab equipment (education only)': 'Lab Equipment',
  'website development (education only)': 'Website Development',
  // Not a suffix case: the BE renamed this one outright. It stays mapped onto the FE official
  // name, which PROCUREMENT_CATEGORY_EXCLUSIONS deliberately hides from the filter list — so this
  // alias makes the two spellings agree without un-hiding anything.
  'adaptive learning courseware': 'Adaptive Learning / Authoring + Platform',
  // The BE's own CATEGORY_ALIASES resolves this spelling to analytics_bi; mirrored here so a
  // `deals.category` of 'Business Intelligence & Institutional Reporting' and a
  // `matched_categories` of 'analytics_bi' land on one option.
  'business intelligence & institutional reporting': 'Analytics & BI',
  'attendance & engagement monitoring': 'Attendance Monitoring & Engagement Tracking',
  'digital preservation & archival systems': 'Digital Preservation & Archives',
  'lecture capture / video management': 'Lecture Capture & Video CMS',
  'library management system (lms)': 'Library & Discovery Services / Reading List Systems',
  'literacy data analytics & dashboards': 'Student Success & Engagement',
  'marketing': 'Student Recruitment & Engagement CRM',
  'marking / grading platforms': 'Marking / Grading Platforms',
  'patron mobile app platform': 'Campus Mobile ID & Commerce',
  'recruitment': 'Student Recruitment & Engagement CRM',
  'remote invigilation platform (ai + human)': 'Remote Proctoring',
  'remote testing / proctoring': 'Remote Proctoring',
  'research admin & grant management': 'Research & Data Management Platforms',
  'rfid & automated materials handling (amh)': 'Library Self-Service, RFID & Automated Handling',
  'software': 'IT Services',
  'software platform (category tbd)': 'IT Services',
  'student information system (sis)': 'Student Information Systems (SIS)',
  'student information systems': 'Student Information Systems (SIS)',
  'academic information system (sis)': 'Student Information Systems (SIS)',
  'student success & analytics': 'Student Success & Engagement',
  'student recruitment & engagement crm': 'Student Recruitment & Engagement CRM',
  'syllabus management (he)': 'Curriculum Enrichment & Curriculum Management Platforms',
  'timetabling, scheduling & space management': 'Timetabling, Scheduling & Space Management',
  'virtual classroom / web conferencing': 'Virtual Classroom & Video-Conferencing',
  'virtual labs / immersive training (vr/ai)': 'Virtual Labs & STEM Simulations',
  'vr learning software & devices (cte)': 'XR / Immersive Learning Platforms',
  'vr training software (public safety)': 'XR / Immersive Learning Platforms',
  'web & digital platforms (edu)': 'Website Development',
  'web & digital platforms (library websites)': 'Website Development',
  'web & digital platforms (virtual library)': 'Website Development',
  'website development': 'Website Development',
  'xr / immersive learning platforms': 'XR / Immersive Learning Platforms',
};

/** Categories hidden from Procurement filters and Product Fit pickers */
export const PROCUREMENT_CATEGORY_EXCLUSIONS = [
  'Defense Technology',
  'Adaptive Learning / Authoring + Platform',
  'ICT Investment',
];

/** Sorted official category labels used in Procurement (static baseline). */
export function getOfficialProcurementCategoryOptions(extra = []) {
  const excluded = new Set(PROCUREMENT_CATEGORY_EXCLUSIONS);
  const merged = [
    ...Object.values(CATEGORY_KEY_TO_OFFICIAL).filter(Boolean),
    ...(extra || []),
  ];
  return [...new Set(merged)]
    .filter((c) => c && !excluded.has(c))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** All categories stored on a product fit profile (primary + tags, deduped). */
export function getProfileProcurementCategories(profile) {
  const tags = Array.isArray(profile?.category_tags) ? profile.category_tags : [];
  const primary = profile?.category_primary ? [profile.category_primary] : [];
  return [...new Set([...primary, ...tags].map((c) => String(c).trim()).filter(Boolean))];
}

/** Set of all official names for pass-through check */
const OFFICIAL_NAMES = new Set(Object.values(CATEGORY_KEY_TO_OFFICIAL));

/**
 * Does this value resolve to a name in the official taxonomy?
 *
 * [2026-10-17] toOfficialCategoryName() returns its INPUT UNCHANGED when it recognises nothing, so
 * a caller cannot tell "resolved" from "gave up" by looking at the result. That is fine for display
 * (showing the raw string beats showing nothing) and wrong for deciding what belongs in a
 * controlled list. `matched_categories` is uncontrolled free text in places — 496 distinct values
 * on live deals, including whole sentences — so anything built from it needs this gate.
 */
export function isOfficialCategoryName(value) {
  return OFFICIAL_NAMES.has(toOfficialCategoryName(value));
}

/** Official name -> category_key for API calls */
const OFFICIAL_TO_KEY = {};
Object.entries(CATEGORY_KEY_TO_OFFICIAL).forEach(([key, official]) => {
  if (official) OFFICIAL_TO_KEY[official] = key;
});

/** Normalize key format: lowercase, replace spaces/hyphens with underscores */
function toKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

/** Normalize for variant lookup: lowercase, collapse multiple spaces */
function toVariantKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Convert raw category from deal table to official name.
 * Works for both deals and closed_deals tables.
 * @param {string|null} rawCategory - Category value from deal (e.g. "lms", "LMS", "proctoring")
 * @returns {string|null} Official name or original if no mapping
 */
export function toOfficialCategoryName(rawCategory) {
  if (rawCategory == null || String(rawCategory).trim() === '') return null;
  const s = String(rawCategory).trim();
  if (!s) return null;

  const key = toKey(s);
  const canonical = canonicalCategoryKey(key);
  if (CATEGORY_KEY_TO_OFFICIAL[canonical]) return CATEGORY_KEY_TO_OFFICIAL[canonical];
  if (CATEGORY_KEY_TO_OFFICIAL[key]) return CATEGORY_KEY_TO_OFFICIAL[key];
  if (OFFICIAL_NAMES.has(s)) return s;
  const variantKey = toVariantKey(s);
  if (CATEGORY_VARIANTS[variantKey]) return CATEGORY_VARIANTS[variantKey];
  const keyNoUnderscore = key.replace(/_/g, '');
  const match = Object.entries(CATEGORY_KEY_TO_OFFICIAL).find(
    ([k]) => k.replace(/_/g, '') === keyNoUnderscore
  );
  if (match) return match[1];
  return s;
}

/**
 * Convert category (official name, internal code, or key) to category_key for API.
 * @param {string} s - Official name, internal code (LMS), or key (lms)
 * @returns {string} Category key for API (e.g. "lms", "sis")
 */
export function toCategoryKeyForApi(s) {
  if (s == null || String(s).trim() === '') return '';
  const str = String(s).trim();
  /** Legacy official labels still stored on prefs / deals */
  if (str === 'Alumni / Advancement') return 'alumni_advancement';
  const key = toKey(str);
  if (CATEGORY_KEY_TO_OFFICIAL[key] || CATEGORY_KEY_ALIASES[key]) {
    return canonicalCategoryKey(key);
  }
  if (OFFICIAL_TO_KEY[str]) return OFFICIAL_TO_KEY[str];
  return str.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Values for comma-delimited `categories` query params (procurement/query, contract-table, aggregate).
 * Official names that contain commas (e.g. Timetabling, Scheduling & Space Management) are omitted;
 * the category_key is sent instead so the server does not split one label into multiple tokens.
 */
export function expandCategoriesForApi(categories = []) {
  return [...new Set(
    (categories || []).flatMap((c) => {
      const official = toOfficialCategoryName(c) || String(c || '').trim();
      const apiKey = toCategoryKeyForApi(official) || toCategoryKeyForApi(c) || null;
      const out = [];
      if (apiKey) out.push(apiKey);
      if (official && !official.includes(',')) out.push(official);
      return out.filter(Boolean);
    }),
  )].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Encoded `categories` query fragment, e.g. for `/api/procurement/query?table=closed&categories=...` */
export function encodeCategoriesQueryParam(categories = []) {
  const apiValues = expandCategoriesForApi(categories);
  return apiValues.length > 0 ? encodeURIComponent(apiValues.join(',')) : '';
}
